import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { EvaluationTypeLabelsEnum, queryKeys } from '@/app/utils/constants';
import { useTranslations } from 'use-intl';
import { DocumentsContextProvider } from '@/app/components/shared/EntityDocuments/DocumentsContext';
import { useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { FolderIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { deleteTenderPackageDocuments } from '@/app/services/tenderPackagesService';
import { ResourceConfig } from '@/app/components/features/evaluations/ResourceView/config';
import { SummarizationNamespaces, DocumentTypes } from '@/app/utils/constants';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
  BreadcrumbMenu,
} from '@/app/ui/components/breadcrumb';
import {
  HeaderBackButton,
  HeaderSidebarTrigger,
  HeaderSidechatButton,
  Header,
  HeaderButton,
} from '@/app/ui/components/header';
import { MainScrollArea } from '@/app/ui/components/main';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { FilesList } from '@/app/components/shared/EntityDocuments/FilesList';
import { Document } from '@/app/types/models';
import { FileUploadDialog } from '@/app/components/shared/EntityDocuments/FileUploadDialog/FileUploadDialog';
import { DocumentAlerts } from '@/app/components/shared/EntityDocuments/DocumentAlerts';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(home)/files/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFiles = useTranslations('pages.files');
  const navigate = useNavigate();
  const { tenderPackageId, projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const config = ResourceConfig[SummarizationNamespaces.TENDER_PACKAGE];
  const [open, setOpen] = useState(false);
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  const handleRowClick = (doc: Document) => {
    navigate({
      to: '/project/$projectId/tender-packages/$tenderPackageId/files/$documentId',
      params: { projectId, tenderPackageId, documentId: doc.id },
    });
  };

  return (
    <DocumentsContextProvider resourceId={tenderPackageId} documentsType={config.documentType}>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu />
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                <span className="line-clamp-1">{project.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <HugeiconsIcon icon={FolderIcon} />
                {t('nav.files')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <HeaderButton aria-label={tFiles('addFiles')} onClick={() => setOpen(true)}>
          <HugeiconsIcon icon={PlusSignIcon} />
        </HeaderButton>
      </Header>
      <MainScrollArea>
        <DocumentAlerts
          resourceId={tenderPackageId}
          summaryType={SummarizationNamespaces.TENDER_PACKAGE}
        />
        <FilesList
          resourceId={tenderPackageId}
          resourceType={DocumentTypes.TENDER_PACKAGE}
          onAddClickAction={() => setOpen(true)}
          deleteDocumentsAction={deleteTenderPackageDocuments}
          detailsQueryKey={[queryKeys.TENDER_PACKAGE_DETAILS, tenderPackageId]}
          showVersions
          onRowClick={handleRowClick}
        />
      </MainScrollArea>
      <FileUploadDialog
        resourceId={tenderPackageId}
        resourceType={DocumentTypes.TENDER_PACKAGE}
        open={open}
        onOpenChange={setOpen}
        detailsQueryKey={[queryKeys.TENDER_PACKAGE_DETAILS, tenderPackageId]}
      />
    </DocumentsContextProvider>
  );
}
